// pages/FlightDetailPage.js — school-portal flight view: full track map, LLM-detected
// activities with CFI annotation (affirm / deny / correct / report missing / landings),
// and a per-school debrief note. Replaces the landing-score view on the Pilot page.
// Helpers (apiFetch/apiPost/apiPut/useAuth) are passed in from App.js to keep this
// file free of the monolith's internals.
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { SyllabusDebriefCard } from './Enrollment';

export const ACT_LABELS = {
  steep_turn: 'Steep Turns', slow_flight: 'Slow Flight', power_off_stall: 'Power-Off Stalls',
  power_on_stall: 'Power-On Stalls', turns_around_point: 'Turns Around a Point',
  ground_reference: 'Ground Reference', eights_on_pylons: 'Eights on Pylons',
  lazy_eight: 'Lazy Eights', chandelle: 'Chandelles', steep_spiral: 'Steep Spiral',
  emergency_descent: 'Emergency Descent', traffic_pattern: 'Traffic Pattern',
  instrument_approach: 'Instrument Approach', hold: 'Holding',
  departure_climb: 'Departure / Climb', cruise: 'Cruise', approach_descent: 'Approach / Descent',
  unknown: 'Unrecognized Activity',
};
const ACT_TRANSIT = new Set(['cruise', 'departure_climb', 'approach_descent']);
const ACT_COUNTABLE = new Set(['instrument_approach', 'hold']);
const COUNT_NOUNS = { instrument_approach: 'approaches', hold: 'holds' };
const OUTCOME_LABELS = { full_stop: 'Full Stop', touch_and_go: 'Touch & Go', low_approach: 'Low Approach', missed: 'Missed' };
const PROCS = ['ILS', 'RNAV (GPS)', 'LOC', 'VOR', 'VOR/DME', 'NDB'];
const ACT_COLORS = {
  steep_turn: '#f97316', slow_flight: '#eab308', power_off_stall: '#ef4444', power_on_stall: '#dc2626',
  turns_around_point: '#8b5cf6', ground_reference: '#a855f7', eights_on_pylons: '#7c3aed',
  lazy_eight: '#ec4899', chandelle: '#db2777', steep_spiral: '#f43f5e', emergency_descent: '#b91c1c',
  traffic_pattern: '#10b981', instrument_approach: '#14b8a6', hold: '#c084fc',
  departure_climb: '#94a3b8', cruise: '#cbd5e1', approach_descent: '#94a3b8', unknown: '#f59e0b',
};
const fmtDur = (s) => { if (s == null) return ''; const m = Math.round(s / 60); return m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m`; };
const segKey = (sg) => `${sg.start_i}-${sg.end_i}`;

// Landings rule mirrored from the PWA / worker (traffic_pattern + terminal approach_descent
// + instrument_approach that reached the runway).
function derivedLandings(segs) {
  let d = 0;
  segs.forEach((sg, i) => {
    if (sg.type === 'traffic_pattern') d += 1;
    else if (sg.type === 'approach_descent' && (i === segs.length - 1 || segs[i + 1]?.type === 'departure_climb')) d += 1;
    else if (sg.type === 'instrument_approach' && ['full_stop', 'touch_and_go'].includes(sg.outcome)) d += 1;
  });
  return d;
}

export default function FlightDetailPage({ apiFetch, apiPost, apiPut, useAuth }) {
  const { sid } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [mapReady, setMapReady] = useState(false);
  const [showTransit, setShowTransit] = useState(false);
  const [hover, setHover] = useState(null);        // segKey being highlighted
  const [showMissing, setShowMissing] = useState(false);
  const [missingSel, setMissingSel] = useState({}); // type -> count
  const [missingLocs, setMissingLocs] = useState({}); // type -> [{airport,runway,proc}]
  const [flash, setFlash] = useState(null);
  const [correcting, setCorrecting] = useState(null); // segKey being corrected
  const [corrForm, setCorrForm] = useState({});
  const [notes, setNotes] = useState('');
  const [actNotes, setActNotes] = useState({});
  const [saveState, setSaveState] = useState('idle'); // idle|dirty|saving|saved|error
  const mapRef = useRef(null);
  const layersRef = useRef({});

  const load = () => apiFetch(`/api/flights/${sid}`, token)
    .then(d => { setData(d); setNotes(d.debrief?.notes || ''); setActNotes(d.debrief?.activity_notes || {}); })
    .catch(e => setErr(e.message)).finally(() => setLoading(false));
  useEffect(() => { setLoading(true); load(); /* eslint-disable-next-line */ }, [sid, token]);
  useEffect(() => { if (/(block|enrollment)=/.test(window.location.search)) setTimeout(() => document.getElementById('syllabus-card')?.scrollIntoView({ behavior: 'smooth' }), 900); }, []);

  // Poll while a re-analysis is pending so the CFI sees the new segments land
  useEffect(() => {
    const claimedNoAnalysis = data && !data.activities && (data.claimants || []).length > 0;
    if (data?.activities?.status !== 'pending' && !claimedNoAnalysis) return;
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [data?.activities?.status]);

  // Leaflet loader (same pattern as the old ScoreDetailPage)
  useEffect(() => {
    if (window.L) { setMapReady(true); return; }
    if (!document.getElementById('leaflet-css')) {
      const css = document.createElement('link'); css.id = 'leaflet-css'; css.rel = 'stylesheet';
      css.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css'; document.head.appendChild(css);
    }
    const js = document.createElement('script'); js.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    js.onload = () => setMapReady(true); document.head.appendChild(js);
  }, []);

  const segs = useMemo(() => (data?.activities?.segments || []).slice().sort((a, b) => a.start_i - b.start_i), [data]);
  const maneuvers = segs.filter(sg => !ACT_TRANSIT.has(sg.type));
  const transit = segs.filter(sg => ACT_TRANSIT.has(sg.type));
  const derived = derivedLandings(segs);
  const landings = data?.activities?.pilot_landings ?? derived;

  // Render / re-render map when data + leaflet ready
  useEffect(() => {
    if (!mapReady || !data?.track?.length) return;
    const el = document.getElementById('flight-map');
    if (!el) return;
    const L = window.L;
    if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; layersRef.current = {}; }
    const track = data.track; // [lat, lon, alt, gs, time, elapsed]
    const coords = track.map(p => [p[0], p[1]]);
    const map = L.map(el, { zoomControl: true }).fitBounds(L.latLngBounds(coords).pad(0.1));
    mapRef.current = map;
    L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', { attribution: 'Esri', maxZoom: 19 }).addTo(map);
    // base track (thin, under segments)
    L.polyline(coords, { color: '#ffffff', weight: 2, opacity: 0.5 }).addTo(map);
    segs.forEach(sg => {
      const s = Math.max(0, sg.start_i), e = Math.min(track.length - 1, sg.end_i);
      if (e <= s) return;
      const isTransit = ACT_TRANSIT.has(sg.type);
      const line = L.polyline(coords.slice(s, e + 1), {
        color: ACT_COLORS[sg.type] || '#f59e0b', weight: isTransit ? 3 : 5, opacity: isTransit ? 0.55 : 0.95,
      }).addTo(map);
      const detail = [sg.airport && sg.runway ? `${sg.airport} RWY ${sg.runway}` : (sg.runway ? `RWY ${sg.runway}` : null),
        sg.outcome ? OUTCOME_LABELS[sg.outcome] : null, sg.min_agl_ft != null ? `${sg.min_agl_ft}ft AGL` : null].filter(Boolean).join(' · ');
      line.bindTooltip(`<b>${ACT_LABELS[sg.type] || sg.type}</b><br/>${sg.start_time}–${sg.end_time}Z · ${fmtDur(sg.duration_s)}${detail ? '<br/>' + detail : ''}`, { sticky: true });
      line.on('mouseover', () => setHover(segKey(sg)));
      line.on('mouseout', () => setHover(null));
      line.on('click', () => { const c = document.getElementById(`seg-${segKey(sg)}`); c && c.scrollIntoView({ behavior: 'smooth', block: 'center' }); });
      layersRef.current[segKey(sg)] = line;
    });
    L.circleMarker(coords[0], { radius: 6, color: '#22c55e', fillColor: '#22c55e', fillOpacity: 0.9 }).addTo(map).bindPopup(`Start ${track[0][4]}Z`);
    L.circleMarker(coords[coords.length - 1], { radius: 6, color: '#f97316', fillColor: '#f97316', fillOpacity: 0.9 }).addTo(map).bindPopup(`End ${track[track.length - 1][4]}Z`);
    return () => { if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; layersRef.current = {}; } };
    // eslint-disable-next-line
  }, [mapReady, data]);

  // Highlight polyline for hovered card
  useEffect(() => {
    Object.entries(layersRef.current).forEach(([k, line]) => {
      const sg = segs.find(s => segKey(s) === k); if (!sg) return;
      const isTransit = ACT_TRANSIT.has(sg.type);
      if (k === hover) { line.setStyle({ weight: 9, opacity: 1 }); line.bringToFront(); }
      else line.setStyle({ weight: isTransit ? 3 : 5, opacity: isTransit ? 0.55 : 0.95 });
    });
  }, [hover, segs]);

  // Annotation maps: the school's own verdicts (this user) + others' for display
  const myId = data?.school_user_id;
  const myVerdicts = {}, otherVerdicts = {};
  (data?.annotations || []).forEach(a => {
    if (a.start_i < 0) return;
    const k = `${a.start_i}-${a.end_i}`;
    if (a.source === 'school' && a.user_id === myId) myVerdicts[k] = a;
    else (otherVerdicts[k] = otherVerdicts[k] || []).push(a);
  });
  const myReports = (data?.annotations || []).filter(a => a.start_i < 0 && a.source === 'school' && a.user_id === myId);

  const vote = async (sg, verdict) => {
    const cur = myVerdicts[segKey(sg)]?.verdict;
    if (cur === verdict) return;
    try {
      await apiPost(`/api/flights/${sid}/verdict`, { start_i: sg.start_i, end_i: sg.end_i, type: sg.type, verdict }, token);
      setData(d => ({ ...d, annotations: [...(d.annotations || []).filter(a => !(a.source === 'school' && a.user_id === myId && a.start_i === sg.start_i && a.end_i === sg.end_i)),
        { start_i: sg.start_i, end_i: sg.end_i, type: sg.type, verdict, source: 'school', user_id: myId, who: 'you' }] }));
    } catch (e) { setFlash(e.message); }
  };
  const submitCorrection = async (sg) => {
    const correction = {};
    ['proc', 'airport', 'runway', 'outcome'].forEach(k => { if (corrForm[k]) correction[k] = corrForm[k]; });
    if (!Object.keys(correction).length) { setCorrecting(null); return; }
    try {
      await apiPost(`/api/flights/${sid}/verdict`, { start_i: sg.start_i, end_i: sg.end_i, type: sg.type, correction }, token);
      setCorrecting(null); setCorrForm({}); load();
    } catch (e) { setFlash(e.message); }
  };
  const setLandings = async (n) => {
    if (n < 0 || n > 99) return;
    try {
      const r = await apiPost(`/api/flights/${sid}/landings`, { count: n, derived }, token);
      setData(d => ({ ...d, activities: { ...d.activities, pilot_landings: n, status: r.reanalyzing ? 'pending' : d.activities.status } }));
      if (r.reanalyzing) setFlash('Landing count differs from detected — re-analysis queued.');
    } catch (e) { setFlash(e.message); }
  };
  const submitMissing = async () => {
    const types = Object.keys(missingSel).filter(t => missingSel[t] > 0);
    const counts = {}; const details = {};
    types.forEach(t => { if (ACT_COUNTABLE.has(t)) { counts[t] = missingSel[t]; const locs = (missingLocs[t] || []).filter(l => l.airport || l.runway || l.proc); if (locs.length) details[t] = locs; } });
    try {
      const r = await apiPost(`/api/flights/${sid}/missing`, { types, counts, details }, token);
      setShowMissing(false); setFlash(r.reanalyzing ? 'Reported — re-analysis queued (usually within ~5 min).' : 'Cleared.');
      load();
    } catch (e) { setFlash(e.message); }
  };
  const saveDebrief = async () => {
    setSaveState('saving');
    try {
      const r = await apiPut(`/api/flights/${sid}/debrief`, { notes, activity_notes: actNotes }, token);
      setData(d => ({ ...d, debrief: { ...(d.debrief || {}), ...r.debrief, author: 'you' } }));
      setSaveState('saved');
    } catch (e) { setSaveState('error'); setFlash(e.message); }
  };
  useEffect(() => { if (flash) { const t = setTimeout(() => setFlash(null), 5000); return () => clearTimeout(t); } }, [flash]);

  if (loading) return <div className="loading-state">Loading flight...</div>;
  if (err || !data?.flight) return <div className="empty-state"><h3>{err || 'Flight not found'}</h3><button className="btn-ghost" onClick={() => navigate(-1)}>← Back</button></div>;

  const f = data.flight, act = data.activities, track = data.track || [];
  const durS = track.length ? track[track.length - 1][5] : null;
  const maxAlt = track.length ? Math.max(...track.map(p => p[2])) : null;
  const iaCount = maneuvers.filter(sg => sg.type === 'instrument_approach').length;
  const holdCount = maneuvers.filter(sg => sg.type === 'hold').length;
  const student = (data.claimants || []).find(c => c.enrolled && ['PIC', 'Student', 'SIC'].includes(c.role)) || (data.claimants || [])[0];

  const SegCard = ({ sg, muted }) => {
    const k = segKey(sg);
    const mine = myVerdicts[k]?.verdict;
    const others = otherVerdicts[k] || [];
    const color = ACT_COLORS[sg.type] || '#f59e0b';
    const detail = [sg.airport && sg.runway ? `${sg.airport} RWY ${sg.runway}` : (sg.runway ? `RWY ${sg.runway}` : null),
      sg.proc || sg.ils_type, sg.outcome ? OUTCOME_LABELS[sg.outcome] : null, sg.min_agl_ft != null ? `${sg.min_agl_ft}ft AGL` : null].filter(Boolean).join(' · ');
    return (
      <div id={`seg-${k}`} onMouseEnter={() => setHover(k)} onMouseLeave={() => setHover(null)}
        style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '10px 12px', borderRadius: 10, border: `1px solid ${hover === k ? color : 'var(--border)'}`,
          background: muted ? 'transparent' : 'var(--bg)', opacity: mine === 'denied' ? 0.55 : 1, transition: 'border-color .1s' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ width: 10, height: 10, borderRadius: 3, background: color, flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: 14 }}>{ACT_LABELS[sg.type] || sg.type}
              {sg.corrected && <span style={{ marginLeft: 6, fontSize: 10, color: '#0d9488', fontWeight: 700 }}>CORRECTED</span>}
            </div>
            <div className="mono" style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              {sg.start_time}–{sg.end_time}Z · {fmtDur(sg.duration_s)}{sg.confidence != null ? ` · ${Math.round(sg.confidence * 100)}%` : ''}
            </div>
            {detail && <div style={{ fontSize: 12, color: '#0d9488', fontWeight: 600 }}>{detail}</div>}
            {sg.why && !muted && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{sg.why}</div>}
            {others.length > 0 && <div style={{ fontSize: 11, marginTop: 2 }}>
              {others.map((a, i) => <span key={i} style={{ marginRight: 8, color: a.verdict === 'denied' ? '#dc2626' : '#16a34a' }}>{a.verdict === 'denied' ? '✗' : '✓'} {a.who || a.source}{a.source === 'app' ? ' (pilot)' : ''}</span>)}
            </div>}
          </div>
          {!muted && <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
            <button onClick={() => vote(sg, 'affirmed')} title="Confirm this activity"
              style={{ width: 36, height: 36, borderRadius: 8, border: `2px solid ${mine === 'affirmed' || mine === 'corrected' ? '#16a34a' : 'var(--border)'}`, background: mine === 'affirmed' || mine === 'corrected' ? '#dcfce7' : 'white', color: '#16a34a', fontWeight: 700, cursor: 'pointer' }}>✓</button>
            <button onClick={() => vote(sg, 'denied')} title="Reject — this didn't happen"
              style={{ width: 36, height: 36, borderRadius: 8, border: `2px solid ${mine === 'denied' ? '#dc2626' : 'var(--border)'}`, background: mine === 'denied' ? '#fee2e2' : 'white', color: '#dc2626', fontWeight: 700, cursor: 'pointer' }}>✗</button>
            {sg.type === 'instrument_approach' && <button className="btn-ghost btn-sm" onClick={() => { setCorrecting(correcting === k ? null : k); setCorrForm({ airport: sg.airport || '', runway: sg.runway || '', outcome: sg.outcome || '', proc: sg.proc || '' }); }}>Edit</button>}
          </div>}
        </div>
        {correcting === k && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', fontSize: 12 }}>
            <input placeholder="APT" value={corrForm.airport} onChange={e => setCorrForm(c => ({ ...c, airport: e.target.value.toUpperCase() }))} style={{ width: 60, padding: 4 }} />
            <input placeholder="RWY" value={corrForm.runway} onChange={e => setCorrForm(c => ({ ...c, runway: e.target.value.toUpperCase() }))} style={{ width: 50, padding: 4 }} />
            <select value={corrForm.proc} onChange={e => setCorrForm(c => ({ ...c, proc: e.target.value }))} style={{ padding: 4 }}><option value="">Type…</option>{PROCS.map(p => <option key={p}>{p}</option>)}</select>
            <select value={corrForm.outcome} onChange={e => setCorrForm(c => ({ ...c, outcome: e.target.value }))} style={{ padding: 4 }}><option value="">Outcome…</option>{Object.entries(OUTCOME_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
            <button className="btn-primary btn-sm" onClick={() => submitCorrection(sg)}>Save</button>
            <button className="btn-ghost btn-sm" onClick={() => setCorrecting(null)}>Cancel</button>
          </div>
        )}
        {!muted && (
          <input placeholder="Instructor note on this activity…" value={actNotes[k] || ''}
            onChange={e => { setActNotes(n => ({ ...n, [k]: e.target.value })); setSaveState('dirty'); }}
            onBlur={() => saveState === 'dirty' && saveDebrief()}
            style={{ fontSize: 12, padding: '6px 8px', border: '1px solid var(--border)', borderRadius: 6, background: 'white' }} />
        )}
      </div>
    );
  };

  return (
    <div className="page">
      {flash && <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 2000, background: '#1e293b', color: 'white', padding: '10px 16px', borderRadius: 8, fontSize: 13 }}>{flash}</div>}
      <div className="page-header">
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button className="btn-ghost" onClick={() => navigate(-1)} style={{ padding: '4px 8px' }}>← Back</button>
            <h1>{f.callsign} · {f.dep_airport || '?'} → {f.arr_airport || '?'}</h1>
          </div>
          <p>{f.flight_date} · {f.ac_type || ''}{track.length ? ` · ${track[0][4]}–${track[track.length - 1][4]}Z` : ''}
            {(data.claimants || []).length > 0 && <> · {data.claimants.map(c => `${c.name} (${c.role})`).join(', ')}</>}
          </p>
        </div>
        {act?.status === 'pending' && <span style={{ background: '#fef3c7', color: '#b45309', padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 700 }}>⏳ Re-analyzing activities…</span>}
        {act?.status === 'done' && <span style={{ background: '#fff7ed', color: '#c2410c', padding: '4px 10px', borderRadius: 8, fontSize: 11, fontWeight: 700, letterSpacing: '.04em' }}>AI BETA</span>}
      </div>

      <div className="stats-grid">
        <div className="stat-card"><div className="stat-value">{landings}</div><div className="stat-label">Landings{act?.pilot_landings != null ? ' (confirmed)' : ''}</div></div>
        <div className="stat-card"><div className="stat-value">{maneuvers.length}</div><div className="stat-label">Maneuvers</div></div>
        <div className="stat-card"><div className="stat-value">{iaCount}{holdCount ? ` / ${holdCount}` : ''}</div><div className="stat-label">Approaches{holdCount ? ' / Holds' : ''}</div></div>
        <div className="stat-card"><div className="stat-value">{durS != null ? fmtDur(durS) : '—'}</div><div className="stat-label">Track time{maxAlt ? ` · max ${maxAlt.toLocaleString()}ft` : ''}</div></div>
      </div>

      {/* Map */}
      <div className="card">
        <h3>🗺️ Flight Track</h3>
        {track.length ? <div id="flight-map" style={{ height: 440, borderRadius: 8, background: '#1a1a2e' }}></div>
          : <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>No track points stored for this flight.</div>}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 8, fontSize: 11, color: 'var(--text-muted)' }}>
          <span>🟢 Start</span><span>🟠 End</span>
          {[...new Set(segs.map(s => s.type))].map(t => <span key={t} style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ width: 10, height: 4, background: ACT_COLORS[t] || '#f59e0b', borderRadius: 2 }} />{ACT_LABELS[t] || t}</span>)}
        </div>
      </div>

      {/* Activities */}
      <div className="card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <h3 style={{ margin: 0 }}>🎯 Activities</h3>
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Maneuvers detected from the track — confirm, reject, or add what's missing to improve detection.</span>
        </div>
        {!act && ((data.claimants || []).length > 0
          ? <div style={{ padding: 20, textAlign: 'center', fontSize: 13, color: '#b45309' }}>⏳ Analysis queued — newly claimed flights are picked up within ~5 minutes. This page refreshes automatically.</div>
          : <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>No activity analysis yet. Flights are analyzed once claimed — by the pilot in the app, a fetch here in the portal, or automatically at dispatch check-in.</div>)}
        {act?.status === 'error' && <div style={{ padding: 12, color: '#b45309', fontSize: 13 }}>Analysis failed: {act.error}</div>}
        {act && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', borderRadius: 10, border: '1px solid var(--border)', background: 'var(--bg)' }}>
              <div><div style={{ fontWeight: 700 }}>Landings</div><div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{act.pilot_landings != null ? `confirmed (detected ${derived})` : 'from detected activities'}</div></div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, border: '2px solid #f59e0b', borderRadius: 24, padding: '2px 6px' }}>
                <button onClick={() => setLandings(landings - 1)} className="btn-ghost" style={{ border: 'none', fontSize: 18, padding: '0 8px' }}>−</button>
                <span className="mono" style={{ fontWeight: 700, fontSize: 16, minWidth: 20, textAlign: 'center' }}>{landings}</span>
                <button onClick={() => setLandings(landings + 1)} className="btn-ghost" style={{ border: 'none', fontSize: 18, padding: '0 8px' }}>+</button>
              </div>
            </div>
            {maneuvers.map(sg => <SegCard key={segKey(sg)} sg={sg} />)}
            {maneuvers.length === 0 && <div style={{ fontSize: 13, color: 'var(--text-muted)', padding: 8 }}>No maneuvers detected — use “What's missing?” to tell the model what was flown.</div>}
            {transit.length > 0 && (
              <button className="btn-ghost" onClick={() => setShowTransit(s => !s)}>{showTransit ? 'Hide' : 'Show'} climb / cruise / approach ({transit.length})</button>
            )}
            {showTransit && transit.map(sg => <SegCard key={segKey(sg)} sg={sg} muted />)}
            <button className="btn-ghost" onClick={() => {
              if (!showMissing) {
                const sel = {}; const locs = {};
                myReports.forEach(r => { sel[r.type] = r.report_count || 1; const c = r.correction; if (c?.locations) locs[r.type] = c.locations; });
                setMissingSel(sel); setMissingLocs(locs);
              }
              setShowMissing(s => !s);
            }}>What's missing?</button>
            {showMissing && (
              <div style={{ padding: 12, borderRadius: 10, border: '1px solid var(--border)', background: 'var(--bg)' }}>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>Pick what was flown but not detected. For approaches and holds, set the <b>total</b> count in the flight; the model re-runs and locates them on the track.</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {(data.missing_types || []).map(t => {
                    const on = (missingSel[t] || 0) > 0;
                    const detected = segs.filter(s => s.type === t).length;
                    return (
                      <div key={t} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 16, border: `1px solid ${on ? '#2563eb' : 'var(--border)'}`, background: on ? '#dbeafe' : 'white', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                        onClick={() => setMissingSel(s => ({ ...s, [t]: on ? 0 : (ACT_COUNTABLE.has(t) ? detected + 1 : 1) }))}>
                        {ACT_LABELS[t]}{!ACT_COUNTABLE.has(t) && detected > 0 ? ` (${detected} found)` : ''}
                        {on && ACT_COUNTABLE.has(t) && <span onClick={e => e.stopPropagation()} style={{ display: 'flex', alignItems: 'center', gap: 4, marginLeft: 6 }}>
                          <button className="btn-ghost btn-sm" onClick={() => setMissingSel(s => ({ ...s, [t]: Math.max(1, s[t] - 1) }))}>−</button>
                          <span className="mono">{missingSel[t]} {COUNT_NOUNS[t]}</span>
                          <button className="btn-ghost btn-sm" onClick={() => setMissingSel(s => ({ ...s, [t]: Math.min(99, s[t] + 1) }))}>+</button>
                        </span>}
                      </div>
                    );
                  })}
                </div>
                {(missingSel.instrument_approach || 0) > 0 && (
                  <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {Array.from({ length: missingSel.instrument_approach }).map((_, i) => {
                      const loc = (missingLocs.instrument_approach || [])[i] || {};
                      const upd = (k, v) => setMissingLocs(m => { const arr = (m.instrument_approach || []).slice(); arr[i] = { ...(arr[i] || {}), [k]: v }; return { ...m, instrument_approach: arr }; });
                      return <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12 }}>
                        <span style={{ width: 120, color: 'var(--text-muted)' }}>Where was approach {i + 1}?</span>
                        <input placeholder="APT" value={loc.airport || ''} onChange={e => upd('airport', e.target.value.toUpperCase())} style={{ width: 60, padding: 4 }} />
                        <input placeholder="RWY" value={loc.runway || ''} onChange={e => upd('runway', e.target.value.toUpperCase())} style={{ width: 50, padding: 4 }} />
                        <select value={loc.proc || ''} onChange={e => upd('proc', e.target.value)} style={{ padding: 4 }}><option value="">Type…</option>{PROCS.map(p => <option key={p}>{p}</option>)}</select>
                      </div>;
                    })}
                  </div>
                )}
                <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                  <button className="btn-primary" onClick={submitMissing}>Report &amp; re-analyze</button>
                  <button className="btn-ghost" onClick={() => setShowMissing(false)}>Cancel</button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div id="syllabus-card" />
      <SyllabusDebriefCard sid={sid} apiFetch={apiFetch} apiPost={apiPost} useAuth={useAuth} onHover={setHover} />

      {/* Debrief */}
      <div className="card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <h3 style={{ margin: 0 }}>📝 Debrief{student ? ` · ${student.name}` : ''}</h3>
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
            {saveState === 'saving' ? 'Saving…' : saveState === 'dirty' ? 'Unsaved changes' : saveState === 'error' ? 'Save failed' :
              data.debrief?.updated_at ? `Saved ${new Date(data.debrief.updated_at).toLocaleString()}${data.debrief.author ? ` by ${data.debrief.author}` : ''}` : 'Not yet written'}
          </span>
        </div>
        <textarea value={notes} onChange={e => { setNotes(e.target.value); setSaveState('dirty'); }}
          placeholder="Instructor notes for this flight — what went well, what to work on next, items to carry into the next lesson…"
          style={{ width: '100%', minHeight: 160, marginTop: 10, padding: 10, border: '1px solid var(--border)', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }} />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
          <button className="btn-primary" disabled={saveState !== 'dirty'} onClick={saveDebrief}>Save debrief</button>
        </div>
      </div>
    </div>
  );
}
